import React, { useState } from 'react'
// libs
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
// hooks
import { useModalData, useShowModal } from 'hooks/index'
// components
import { ColorSelect } from 'components/FormElements'
import { CloseIcon, TrashIcon } from 'components/UI/Icons'
import { Button, BUTTON_LAYOUTS, Modal } from 'components/UI'
// constants
import { Modals } from 'constants/modals'
// types
import { MatrixOption } from 'components/Modals/MatrixModal/components/Matrix/types'
// utils
import { v4 as uuid } from 'uuid'
import { get } from 'lodash'
// views
import { AddButton, ButtonsContainer, Container, DragIcon, Header, Option, Scrollbar, Value } from './views'
// themes
import { colors } from 'themes/index'

interface Props {
  initialOptions: MatrixOption[]
  onSave: (options: MatrixOption[]) => void
}

const MatrixOptionsModal: React.FC<Props> = ({ initialOptions, onSave }) => {
  const [options, setOptions] = useState(initialOptions)

  const {
    hideModal,
    modalData: { isOpen = false }
  } = useModalData({ modalType: Modals.RISK_MATRIX_OPTIONS_MODAL })

  const { showModal: showConfirmModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_MATRIX_ITEM_MODAL })

  // methods
  const reorderOptions = (list: MatrixOption[], startIndex: number, endIndex: number) => {
    const reorderedList = [...list]

    const [removed] = reorderedList.splice(startIndex, 1)
    reorderedList.splice(endIndex, 0, removed)
    return reorderedList
  }

  const onDragEnd = ({ source, destination }) => {
    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId) {
      setOptions((prev) => reorderOptions(prev, source.index, destination.index))
    }
  }

  const onSaveClick = () => {
    onSave(options.filter(({ value, label }) => value && label))
    hideModal()
  }

  const createOption = () =>
    setOptions((prev) => [...prev, { id: uuid(), value: null, label: null, color: colors.gray250 }])

  const deleteOption = (e, idx) => {
    e.stopPropagation()

    showConfirmModal({
      entity: 'option',
      width: 'xs',
      handleConfirm: () =>
        setOptions((prev) => {
          prev.splice(idx, 1)
          return [...prev]
        })
    })
  }

  const onColorChange = (idx: number) => (color: string) =>
    setOptions((prev) =>
      prev.map((item, i) => {
        if (i === idx) {
          return { ...item, color }
        }
        return item
      })
    )

  const onInputBlur = (e, idx, key) => {
    const value = get(e.target, 'value', null)

    setOptions((prev) =>
      prev.map((item, i) => {
        if (idx === i) {
          return { ...item, [key]: value }
        }
        return item
      })
    )
  }

  React.useEffect(() => {
    if (!isOpen) {
      setOptions(initialOptions)
    }
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} width='sm' hideModal={hideModal}>
      <Container>
        <Header>
          <span>Label</span> <span>Internal Value</span> <span>Color</span>
          <CloseIcon onClick={hideModal} />
        </Header>
        <Scrollbar>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={'MATRIX_OPTIONS'}>
              {(provided) => (
                <div ref={provided.innerRef}>
                  {options.map((option, idx) => (
                    <Draggable key={option.id} draggableId={option.id} index={idx} isDragDisabled={false}>
                      {(provided) => (
                        <Option
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={provided.draggableProps.style as React.CSSProperties}>
                          <Value>
                            <span {...provided.dragHandleProps}>
                              <DragIcon />
                            </span>
                            <input
                              type='text'
                              placeholder='set'
                              defaultValue={option.label}
                              onBlur={(e) => onInputBlur(e, idx, 'label')}
                            />
                          </Value>
                          <Value>
                            <input
                              type='number'
                              placeholder='set'
                              defaultValue={option.value}
                              onBlur={(e) => onInputBlur(e, idx, 'value')}
                            />
                          </Value>
                          <ColorSelect
                            initialColor={option.color}
                            onChange={onColorChange(idx)}
                            trigger={<Value color={option.color}>{option.color || 'set'}</Value>}
                          />
                          <TrashIcon onClick={(e) => deleteOption(e, idx)} />
                        </Option>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Scrollbar>

        <AddButton onClick={createOption}>+ add option</AddButton>
        <ButtonsContainer>
          <Button shadowLess type='button' onClick={onSaveClick} layout={BUTTON_LAYOUTS.PURPLE}>
            Save
          </Button>
          <Button shadowLess layout={BUTTON_LAYOUTS.PURPLE_INVERTED} onClick={hideModal}>
            Cancel
          </Button>
        </ButtonsContainer>
      </Container>
    </Modal>
  )
}

export default MatrixOptionsModal
